import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  Subscription
} from 'rxjs';
import {
  AuthService,
  nonEmpty
} from '@app/components/login.component/service/auth.service';
import { MenuService } from './app.menu.service';
import { AppSidebarComponent } from './app.sidebar.component';
import { AppTopbarComponent } from './app.topbar.component';
import { LayoutService } from './service/app.layout.service';
import { UiPreferencesLandingService } from './ui-preferences/ui-preferences-landing.service';

@Component({
  selector: 'app-layout',
  templateUrl: './app.layout.component.html'
})
export class AppLayoutComponent implements OnInit, OnDestroy {
  overlayMenuOpenSubscription: Subscription;

  topbarMenuOpenSubscription: Subscription;

  menuProfileOpenSubscription: Subscription;

  menuOutsideClickListener: (() => void) | null;

  menuScrollListener: (() => void) | null;

  topbarMenuOutsideClickListener: (() => void) | null;

  menuProfileOutsideClickListener: (() => void) | null;

  @ViewChild(AppSidebarComponent) appSidebar!: AppSidebarComponent;

  @ViewChild(AppTopbarComponent) appTopbar!: AppTopbarComponent;

  private userSubscription?: Subscription;
  private userChangeSubscription?: Subscription;

  constructor(
    private menuService: MenuService,
    public layoutService: LayoutService,
    public renderer: Renderer2,
    public router: Router,
    private cd: ChangeDetectorRef,
    private authService: AuthService,
    private landingService: UiPreferencesLandingService
  ) {
    this.hideMenuProfile();

    this.overlayMenuOpenSubscription =
      this.layoutService.overlayOpen$.subscribe(() => {
        this.hideTopbarMenu();

        if (!this.menuOutsideClickListener) {
          this.menuOutsideClickListener = this.renderer.listen(
            'document',
            'click',
            (event) => {
              const isOutsideClicked = !(
                this.appSidebar.el.nativeElement.isSameNode(event.target) ||
                this.appSidebar.el.nativeElement.contains(event.target) ||
                this.appTopbar.menuButton.nativeElement.isSameNode(
                  event.target
                ) ||
                this.appTopbar.menuButton.nativeElement.contains(event.target)
              );
              if (isOutsideClicked) {
                this.hideMenu();
              }
            }
          );
        }

        if (
          (this.layoutService.isHorizontal() || this.layoutService.isSlim()) &&
          !this.menuScrollListener
        ) {
          this.menuScrollListener = this.renderer.listen(
            this.appSidebar.menuContainer.nativeElement,
            'scroll',
            () => {
              if (this.layoutService.isDesktop()) {
                this.hideMenu();
              }
            }
          );
        }

        if (this.layoutService.state.staticMenuMobileActive) {
          this.blockBodyScroll();
        }
      });

    this.topbarMenuOpenSubscription =
      this.layoutService.topbarMenuOpen$.subscribe(() => {
        if (!this.topbarMenuOutsideClickListener) {
          this.topbarMenuOutsideClickListener = this.renderer.listen(
            'document',
            'click',
            (event) => {
              const isOutsideClicked = !(
                this.appTopbar.el.nativeElement.isSameNode(event.target) ||
                this.appTopbar.el.nativeElement.contains(event.target) ||
                this.appTopbar.mobileMenuButton.nativeElement.isSameNode(
                  event.target
                ) ||
                this.appTopbar.mobileMenuButton.nativeElement.contains(
                  event.target
                )
              );
              if (isOutsideClicked) {
                this.hideTopbarMenu();
              }
            }
          );
        }

        if (this.layoutService.state.staticMenuMobileActive) {
          this.blockBodyScroll();
        }
      });

    this.menuProfileOpenSubscription =
      this.layoutService.menuProfileOpen$.subscribe(() => {
        this.hideMenu();

        if (!this.menuProfileOutsideClickListener) {
          this.menuProfileOutsideClickListener = this.renderer.listen(
            'document',
            'click',
            (event) => {
              const isOutsideClicked = !(
                this.appSidebar.menuProfile.el.nativeElement.isSameNode(
                  event.target
                ) ||
                this.appSidebar.menuProfile.el.nativeElement.contains(
                  event.target
                )
              );
              if (isOutsideClicked) {
                this.hideMenuProfile();
              }
            }
          );
        }
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.hideMenu();
        this.hideTopbarMenu();
        this.hideMenuProfile();
      });
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp(
          '(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)',
          'gi'
        ),
        ' '
      );
    }
  }

  hideMenu() {
    this.layoutService.state.overlayMenuActive = false;
    this.layoutService.state.staticMenuMobileActive = false;
    this.layoutService.state.menuHoverActive = false;
    this.menuService.reset();

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }

    if (this.menuScrollListener) {
      this.menuScrollListener();
      this.menuScrollListener = null;
    }
    this.unblockBodyScroll();
  }

  hideTopbarMenu() {
    this.layoutService.state.topbarMenuActive = false;

    if (this.topbarMenuOutsideClickListener) {
      this.topbarMenuOutsideClickListener();
      this.topbarMenuOutsideClickListener = null;
    }
  }

  hideMenuProfile() {
    this.layoutService.state.menuProfileActive = false;

    if (this.menuProfileOutsideClickListener) {
      this.menuProfileOutsideClickListener();
      this.menuProfileOutsideClickListener = null;
    }
  }

  get containerClass() {
    const styleClass: { [key: string]: boolean } = {
      'layout-overlay': this.layoutService.config.menuMode === 'overlay',
      'layout-static': this.layoutService.config.menuMode === 'static',
      'layout-slim': this.layoutService.config.menuMode === 'slim',
      'layout-sidebar': this.layoutService.config.menuMode === 'sidebar',
      'layout-horizontal': this.layoutService.config.menuMode === 'horizontal',
      'layout-reveal': this.layoutService.config.menuMode === 'reveal',
      'p-input-filled': this.layoutService.config.inputStyle === 'filled',
      'p-ripple-disabled': !this.layoutService.config.ripple,
      'layout-static-inactive':
        this.layoutService.state.staticMenuDesktopInactive &&
        this.layoutService.config.menuMode === 'static',
      'layout-overlay-active': this.layoutService.state.overlayMenuActive,
      'layout-mobile-active': this.layoutService.state.staticMenuMobileActive,
      'layout-topbar-menu-active': this.layoutService.state.topbarMenuActive,
      'layout-menu-profile-active': this.layoutService.state.menuProfileActive,
      'layout-reveal-active': this.layoutService.state.revealMenuActive,
      'layout-reveal-anchored': this.layoutService.state.anchored
    };

    styleClass['layout-topbar-' + this.layoutService.config.topbarTheme] = true;
    styleClass['layout-menu-' + this.layoutService.config.menuTheme] = true;
    styleClass[
      'layout-menu-profile-' + this.layoutService.config.menuProfilePosition
    ] = true;
    return styleClass;
  }

  ngOnInit(): void {
    // GAP-UC-43 (OQ-CI-3 RESOLVED, bug fix 2026-09-23): apply saved UI preferences once per
    // AUTHENTICATED USER, not once per page load. `UiPreferencesLandingService` is a root
    // singleton, so its one-shot `applied` guard survives a logout->login within the same SPA
    // session (the page never reloads). ROOT CAUSE of the reported bug: after User A logged in
    // (consuming the guard), logging in as User B never re-fetched preferences (no GET request
    // fired) and the shell kept showing User A's already-applied menu-state/landing — a
    // cross-user leak, not just a missed apply. Fix: track the authenticated user's identity
    // (`username`) and call `landingService.reset()` on every change (including the very first
    // login) so a fresh GET + apply is attempted for that specific user, before the (root-timing)
    // apply trigger below runs.
    const userChanged$ = this.authService.user$.pipe(
      filter((user) => nonEmpty(user)),
      distinctUntilChanged(
        (a, b) =>
          (a as { username?: string }).username ===
          (b as { username?: string }).username
      )
    );
    this.userChangeSubscription = userChanged$.subscribe(() =>
      this.landingService.reset()
    );

    // This must fire BOTH on a page refresh (user$ already populated) AND right after login (the
    // shell mounts and the router navigates to '' asynchronously). Relying on the user$ emission
    // alone missed the post-login case, because the emission could arrive before the navigation
    // to the app root settled. So we combine the authenticated user with router navigation events
    // and (re)attempt the one-shot apply whenever we are at the app root. applyOnce() is
    // idempotent per the guard above: it only consumes its one-shot guard once it actually
    // applies at the root, and no-ops on a deep link / mustChangePassword — so repeated triggers
    // are safe.
    const navToRoot$ = this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url)
    );
    this.userSubscription = combineLatest([
      this.authService.user$.pipe(filter((user) => nonEmpty(user))),
      navToRoot$
    ]).subscribe(([user]) => {
      if (this.authService.mustChangePassword(user)) {
        return; // coexist with forced password change — do not apply landing yet.
      }
      void this.landingService.applyOnce();
    });
  }

  ngOnDestroy() {
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }

    if (this.userChangeSubscription) {
      this.userChangeSubscription.unsubscribe();
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }
  }
}
